import { Coins, Server, TrendingUp, Zap } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { Skeleton } from '../../ui';
import { ProviderType, type UsageSummary } from '../../gql/types';
import { providerLabel } from '../shared/providerLabels';
import { formatCompact, formatCost, formatNumber, formatPercent } from '../../lib/format';

// Usage dashboard headline metrics (US-8.5.1). Three things matter most:
//   1. OWNED-INFRA SHARE — the IN_HOUSE provider's requests / total. This is the product's core
//      value prop (how much traffic we keep on our own infrastructure), so it leads, big.
//   2. PER-PROVIDER FAILURE RATES — from byProvider[].failureRate.
//   3. TOKEN VOLUME — totalTokens, with a prompt/completion-aware breakdown via byProvider tokens.
//
// The byProvider breakdown is visualized with an accessible, dependency-free share bar (data-viz
// as part of the design system, not a chart lib).

/** Gradient + solid colors per provider for the share viz. In-house leads with the brand accent. */
const PROVIDER_VIZ: Record<ProviderType, { bar: string; dot: string }> = {
  [ProviderType.InHouse]: { bar: 'bg-gradient-to-r from-indigo-500 to-purple-600', dot: 'bg-indigo-500' },
  [ProviderType.Openai]: { bar: 'bg-emerald-500', dot: 'bg-emerald-500' },
  [ProviderType.Anthropic]: { bar: 'bg-amber-500', dot: 'bg-amber-500' },
  [ProviderType.Gemini]: { bar: 'bg-sky-500', dot: 'bg-sky-500' },
  [ProviderType.Openrouter]: { bar: 'bg-rose-500', dot: 'bg-rose-500' },
};

/** Compute the owned-infra (IN_HOUSE) request share as a 0..1 fraction. */
export function ownedInfraShare(summary: UsageSummary): number {
  if (summary.totalRequests <= 0) return 0;
  const inHouse = summary.byProvider.find((b) => b.provider === ProviderType.InHouse);
  if (!inHouse) return 0;
  return inHouse.requests / summary.totalRequests;
}

function MetricCard({
  icon,
  label,
  value,
  sub,
  accent,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  accent: string;
  children?: React.ReactNode;
}) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <article
      className={`relative overflow-hidden rounded-2xl border p-6 ${
        isDark
          ? 'border-white/[0.06] bg-gradient-to-br from-white/[0.05] to-white/[0.02]'
          : 'border-slate-200 bg-white shadow-sm'
      }`}
    >
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full ${accent} opacity-[0.14] blur-2xl`}
      />
      <div className="relative flex items-center gap-2.5">
        <span className={isDark ? 'text-white/40' : 'text-slate-400'} aria-hidden="true">
          {icon}
        </span>
        <span
          className={`text-[11px] font-medium uppercase tracking-widest ${
            isDark ? 'text-white/40' : 'text-slate-400'
          }`}
        >
          {label}
        </span>
      </div>
      <p
        className={`relative mt-3 text-4xl font-bold tabular-nums tracking-tight ${
          isDark ? 'text-white' : 'text-slate-900'
        }`}
      >
        {value}
      </p>
      {sub && (
        <p className={`relative mt-1 text-xs ${isDark ? 'text-white/40' : 'text-slate-500'}`}>
          {sub}
        </p>
      )}
      {children}
    </article>
  );
}

/** Accessible, dependency-free horizontal share bar for the byProvider token breakdown. */
function ShareBar({ summary }: { summary: UsageSummary }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const total = summary.byProvider.reduce((acc, b) => acc + b.tokens, 0);
  const segments = summary.byProvider
    .filter((b) => b.tokens > 0)
    .map((b) => ({ ...b, fraction: total > 0 ? b.tokens / total : 0 }));

  return (
    <div className="mt-4">
      <div
        className={`flex h-3 w-full overflow-hidden rounded-full ${
          isDark ? 'bg-white/[0.06]' : 'bg-slate-100'
        }`}
        role="img"
        aria-label={`Token volume by provider: ${segments
          .map((s) => `${providerLabel(s.provider)} ${formatPercent(s.fraction)}`)
          .join(', ')}`}
      >
        {segments.map((s) => (
          <div
            key={s.provider}
            className={`h-full ${PROVIDER_VIZ[s.provider].bar}`}
            style={{ width: `${s.fraction * 100}%` }}
            data-testid={`share-segment-${s.provider}`}
          />
        ))}
      </div>
      <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
        {segments.map((s) => (
          <li key={s.provider} className="flex items-center gap-2 text-xs">
            <span
              className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${PROVIDER_VIZ[s.provider].dot}`}
              aria-hidden="true"
            />
            <span className={isDark ? 'text-white/70' : 'text-slate-600'}>
              {providerLabel(s.provider)}
            </span>
            <span className={`tabular-nums ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
              {formatCompact(s.tokens)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Per-provider failure-rate list (US-8.5.1). Higher rates are emphasized in rose. */
function FailureRates({ summary }: { summary: UsageSummary }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <ul className="mt-4 space-y-2.5" data-testid="failure-rates">
      {summary.byProvider.map((b) => {
        const elevated = b.failureRate >= 0.05;
        return (
          <li key={b.provider} className="flex items-center justify-between gap-3 text-sm">
            <span className={isDark ? 'text-white/70' : 'text-slate-600'}>
              {providerLabel(b.provider)}
            </span>
            <span
              className={`tabular-nums font-semibold ${
                elevated
                  ? isDark
                    ? 'text-rose-300'
                    : 'text-rose-600'
                  : isDark
                    ? 'text-emerald-300'
                    : 'text-emerald-600'
              }`}
            >
              {formatPercent(b.failureRate)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export interface SummaryMetricsProps {
  summary: UsageSummary | null;
  loading: boolean;
}

export function SummaryMetrics({ summary, loading }: SummaryMetricsProps) {
  if (loading && !summary) {
    return (
      <div
        className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3"
        data-testid="summary-loading"
      >
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="block" height={180} />
        ))}
      </div>
    );
  }

  if (!summary) return null;

  const share = ownedInfraShare(summary);

  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
      <MetricCard
        icon={<Server className="h-4 w-4" />}
        label="Owned-infra share"
        value={formatPercent(share, { digits: 0 })}
        sub="Requests served on in-house infrastructure"
        accent="bg-gradient-to-br from-indigo-500 to-purple-600"
      />
      <MetricCard
        icon={<Zap className="h-4 w-4" />}
        label="Token volume"
        value={formatCompact(summary.totalTokens)}
        sub={`${formatNumber(summary.totalRequests)} requests`}
        accent="bg-gradient-to-br from-emerald-500 to-teal-600"
      >
        <ShareBar summary={summary} />
      </MetricCard>
      <MetricCard
        icon={<TrendingUp className="h-4 w-4" />}
        label="Reliability & cost"
        value={
          <span className="flex items-baseline gap-2">
            <Coins className="h-6 w-6 opacity-40" aria-hidden="true" />
            <span>{formatCost(summary.totalCostEstimate)}</span>
            <span className="text-xs font-normal opacity-50">est. cost</span>
          </span>
        }
        sub="Failure rate by provider"
        accent="bg-gradient-to-br from-rose-500 to-red-600"
      >
        <FailureRates summary={summary} />
      </MetricCard>
    </div>
  );
}
